import { Moment as OriginalMoment } from 'moment';
import { extendMoment, MomentRangeExtends } from 'moment-range';
import momentTZ from 'moment-timezone';
import React, { useMemo } from 'react';

import contrastYIQ from '../../utils/contrastYIQ';
import { IWarningDatum } from './types';
import { CardContent, CardContentWrapper, Container, Heading, Title, WarningValue } from './WarningCard.style';

const moment = extendMoment(momentTZ as unknown as OriginalMoment) as unknown as MomentRangeExtends & typeof momentTZ;

const DEFAULT_THRESHOLD_COLOUR = '#7F7F7F';
const DEFAULT_THRESHOLD_BG_COLOUR = '#FFFFFF';

const getTextColor = (color: string, blockBackgroundColor?: boolean) => (blockBackgroundColor ? contrastYIQ(color) : color);

interface WarningCardProps {
    fontSize: number;
    card: IWarningDatum;
    blockBackgroundColor?: boolean;
}

const WarningCard: React.FC<WarningCardProps> = ({ card, fontSize, blockBackgroundColor }) => {
    const { aggregateLabel, label, displayValue, value, threshold, unitLabel } = card;

    const duration = moment.duration(card.duration, 'm').asHours();
    const hours = duration === 1 ? 'hour' : 'hours';

    const warningBackgroundColor = threshold?.color || DEFAULT_THRESHOLD_BG_COLOUR;
    const thresholdAlertColor = threshold?.color || DEFAULT_THRESHOLD_COLOUR;

    const descriptionColor =
        warningBackgroundColor === DEFAULT_THRESHOLD_BG_COLOUR || !blockBackgroundColor ? '#333' : getTextColor(thresholdAlertColor, blockBackgroundColor);

    const valueColor = warningBackgroundColor === DEFAULT_THRESHOLD_BG_COLOUR ? thresholdAlertColor : getTextColor(thresholdAlertColor, blockBackgroundColor);

    // Block colour threshold or getTextColor
    const thresholdDescription = useMemo(
        () =>
            threshold ? (
                <span>
                    {[threshold.label || threshold.comparator, ' ', threshold.value, ' ', unitLabel].map((content, i) => (
                        <React.Fragment key={i}>{content}</React.Fragment>
                    ))}
                </span>
            ) : null,
        [threshold, unitLabel]
    );

    const thresholdHeading = threshold?.name || null;

    const warningValueTitle = displayValue ? (
        <Title>
            <WarningValue fontSize={fontSize} color={valueColor}>
                {`${displayValue || value} `}
                {unitLabel}
            </WarningValue>
        </Title>
    ) : (
        '---'
    );

    return (
        <Container fontSize={fontSize} title={warningValueTitle} bordered background={blockBackgroundColor ? warningBackgroundColor : 'white'}>
            <CardContentWrapper className='card-body'>
                <Heading fontSize={fontSize} colour={valueColor}>
                    {thresholdHeading ? thresholdHeading : 'NO WARNINGS'}
                </Heading>
                <CardContent colour={descriptionColor} fontSize={fontSize}>
                    {`${aggregateLabel} `}
                    <strong>{`${label} `}&nbsp;</strong>
                    over the next
                    <strong>
                        {' '}
                        {duration} {hours}
                    </strong>
                    <span>&nbsp;{thresholdDescription}</span>
                </CardContent>
            </CardContentWrapper>
        </Container>
    );
};

export default WarningCard;
